import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

// Components
import Iconify from 'src/components/Iconify';
import { RowInput } from 'src/components/forms/RowInput';
import PhoneInput from 'react-phone-number-input'

// UI Components
import { Button } from 'src/@/components/ui/button';
import { Input } from 'src/@/components/ui/input';
import { Separator } from 'src/@/components/ui/separator';
import { LoadingButton } from 'src/@/components/ui/loading-button';
import {
    Command,
    CommandEmpty,
    CommandGroup,
    CommandInput,
    CommandItem,
    CommandList,
} from "src/@/components/ui/command"
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "src/@/components/ui/popover"

// Actions & Utils
import buscarCep from 'src/actions/chats/buscarCep';
import { formataNumeroCelular, maskCPF, maskDate } from 'src/utils/functions';
import v2_actionPesquisarContatos from 'src/actions/v2/contatos/v2_actionPesquisarContatos';
import v2_actionEditarContato from 'src/actions/v2/contatos/v2_actionEditarContato';
import { getChatInfoByChatId } from 'src/redux/slices/chat';
import { cn } from "src/@/lib/utils"

// ----------------------------------------------------------------------

ChatInfoContatoForm.propTypes = {
    cliente: PropTypes.object,
    lead: PropTypes.object,
    onOpenModalNovoContato: PropTypes.func,
    onEditarContatoId: PropTypes.func
};

export default function ChatInfoContatoForm({
    cliente,
    lead,
    onOpenModalNovoContato,
    onEditarContatoId
}) {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // State
    const [open, setOpen] = useState(false);
    const [listaContatos, setListaContatos] = useState([]);
    const [pesquisa, setPesquisa] = useState('');
    const [contactId, setContactId] = useState(lead?.contato);
    const [isChange, setIsChange] = useState(null);
    const [loadingEditarContato, setLoadingEditarContato] = useState(false);

    const [contato, setContato] = useState({
        nome: lead?.contato?.nome || '',
        email: lead?.contato?.info?.email || '',
        whatsapp: lead?.contato?.telefone || '',
        dataNascimento: lead?.contato?.info?.dataNascimento || '',
        cpf: lead?.contato?.info?.cpf || ''
    });

    const [endereco, setEndereco] = useState({
        cep: lead?.contato?.info?.endereco?.cep || '',
        rua: lead?.contato?.info?.endereco?.rua || '',
        numero: lead?.contato?.info?.endereco?.numero || '',
        bairro: lead?.contato?.info?.endereco?.bairro || '',
        cidade: lead?.contato?.info?.endereco?.cidade || '',
        estado: lead?.contato?.info?.endereco?.estado || '',
        complemento: lead?.contato?.info?.endereco?.complemento || ''
    });

    useEffect(() => {
        setContactId(lead?.contato);
        if (lead?.contato) {
            setContato({
                nome: lead.contato.nome || '',
                email: lead.contato.info?.email || '',
                whatsapp: lead.contato.telefone || '',
                dataNascimento: lead.contato.info?.dataNascimento || '',
                cpf: lead.contato.info?.cpf || ''
            });
            setEndereco({
                cep: lead.contato.info?.endereco?.cep || '',
                rua: lead.contato.info?.endereco?.rua || '',
                numero: lead.contato.info?.endereco?.numero || '',
                bairro: lead.contato.info?.endereco?.bairro || '',
                cidade: lead.contato.info?.endereco?.cidade || '',
                estado: lead.contato.info?.endereco?.estado || '',
                complemento: lead.contato.info?.endereco?.complemento || ''
            });
        }
    }, [lead]);

    async function pesquisarContatos(valor) {
        try {
            const result = await v2_actionPesquisarContatos(cliente._id, valor)
            setListaContatos(result.data.data)
        } catch (error) {
            console.log(error);
        }
    }

    async function handleEditarContato() {
        setLoadingEditarContato(true);
        try {
            const data = {
                ...contato,
                info: {
                    ...contactId?.info,
                    email: contato.email,
                    dataNascimento: contato.dataNascimento,
                    cpf: contato.cpf,
                    endereco: {
                        ...endereco
                    }
                }
            };
            await v2_actionEditarContato(contactId._id, data);
            setLoadingEditarContato(false);
            setIsChange(false);
            enqueueSnackbar('Contato alterado com sucesso', {
                variant: 'success',
                autoHideDuration: 3000,
                anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            });
            dispatch(getChatInfoByChatId(lead._id));
        } catch (error) {
            setLoadingEditarContato(false);
            console.log(error);
            enqueueSnackbar('Erro ao editar contato', { variant: 'error' });
        }
    }

    const handleOnBlurSearchCep = async (cep) => {
        if (cep && (cep.length === 8 || cep.length === 9)) {
            try {
                const response = await buscarCep(cep);
                setEndereco({
                    ...endereco,
                    rua: response.data.logradouro,
                    bairro: response.data.bairro,
                    cidade: response.data.localidade,
                    estado: response.data.uf,
                    cep: response.data.cep
                });
                setIsChange(true);
            } catch (error) {
                console.log(error);
            }
        }
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="w-full">
                <div className="flex items-center gap-2">
                    <Popover open={open} onOpenChange={setOpen}>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                role="combobox"
                                aria-expanded={open}
                                className="w-[250px] justify-between text-xs font-normal h-10 px-3"
                            >
                                {contactId
                                    ? ((contactId.nome ? `${contactId.nome} - ` : "") + formataNumeroCelular(contactId.telefone))
                                    : "Escolha um contato"}
                                <Iconify icon="lucide:chevrons-up-down" className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[250px] p-0" align="start">
                            <Command shouldFilter={false}>
                                <CommandInput
                                    placeholder="Pesquise por nome ou telefone..."
                                    value={pesquisa}
                                    onValueChange={(val) => {
                                        setPesquisa(val);
                                        if (val.length > 3) {
                                            pesquisarContatos(val);
                                        } else {
                                            setListaContatos([]);
                                        }
                                    }}
                                />
                                <CommandList>
                                    <CommandEmpty className="p-2">
                                        <div className="flex flex-col gap-2 items-center">
                                            <p className="text-[10px] text-slate-500 text-center">
                                                Nenhum contato encontrado.
                                            </p>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-primary text-[10px]"
                                                onClick={() => {
                                                    setOpen(false);
                                                    onOpenModalNovoContato();
                                                }}
                                            >
                                                <Iconify icon="gg:add" className="mr-1 h-3 w-3" />
                                                Criar Contato
                                            </Button>
                                        </div>
                                    </CommandEmpty>
                                    <CommandGroup>
                                        {listaContatos.map((item) => (
                                            <CommandItem
                                                key={item._id}
                                                value={item._id}
                                                onSelect={() => {
                                                    setContactId(item);
                                                    onEditarContatoId(item._id);
                                                    dispatch(getChatInfoByChatId(lead._id));
                                                    setOpen(false);
                                                }}
                                                className="text-xs"
                                            >
                                                <Iconify
                                                    icon="lucide:check"
                                                    className={cn(
                                                        "mr-2 h-3 w-3",
                                                        contactId?._id === item._id ? "opacity-100" : "opacity-0"
                                                    )}
                                                />
                                                {item.nome ? `${item.nome} - ` : ""}{formataNumeroCelular(item.telefone)}
                                            </CommandItem>
                                        ))}
                                    </CommandGroup>
                                </CommandList>
                            </Command>
                        </PopoverContent>
                    </Popover>

                    {!contactId && (
                        <Button
                            variant="ghost"
                            size="icon"
                            onClick={onOpenModalNovoContato}
                            className="h-10 w-10 text-slate-600 hover:text-primary"
                        >
                            <Iconify icon="zondicons:add-solid" className="w-5 h-5" />
                        </Button>
                    )}
                </div>
            </div>

            <Separator className="my-2 border-dashed" />


        </div>
    );
}
